import { useState, useEffect, useCallback } from 'react';

type DialogStep = 'select-policies' | 'select-delivery';

interface UseSendPuiDialogProps {
  open: boolean;
  onSend: (options: SendAnonymousPuiOptions) => void;
  onClose: () => void;
  availablePolicies: PolicyInfo[];
  isPending: boolean;
  isSuccess: boolean;
}

export function useSendPuiDialog({
  open,
  onSend,
  onClose,
  availablePolicies,
  isPending,
  isSuccess,
}: UseSendPuiDialogProps) {
  const [step, setStep] = useState<DialogStep>('select-policies');
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(new Set());
  const [showLoadingSequence, setShowLoadingSequence] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('select-policies');
      setSelectedPolicies(new Set());
      setShowLoadingSequence(false);
    }
  }, [open]);

  const handleTogglePolicy = useCallback((uniqueKey: string) => {
    setSelectedPolicies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uniqueKey)) {
        newSet.delete(uniqueKey);
      } else {
        newSet.add(uniqueKey);
      }
      return newSet;
    });
  }, []);

  const handleContinue = useCallback(() => {
    if (selectedPolicies.size > 0) {
      setStep('select-delivery');
    }
  }, [selectedPolicies.size]);

  const handleBack = useCallback(() => {
    setStep('select-policies');
  }, []);

  const handleSend = useCallback(
    (sendToSelf?: boolean) => {
      const selectedPolicyData = availablePolicies
        .filter(policy => selectedPolicies.has(policy.uniqueKey))
        .map(policy => ({
          productName: policy.productName,
          fundingPolicyType: policy.fundingPolicyType,
          isJoint: policy.isJoint,
        }));

      setShowLoadingSequence(true);
      onSend({
        policies: selectedPolicyData,
        sendToSelf,
      });
    },
    [availablePolicies, selectedPolicies, onSend],
  );

  const handleLoadingComplete = useCallback(() => {
    setShowLoadingSequence(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isSuccess && !isPending) {
      handleLoadingComplete();
      onClose();
    }
  }, [isSuccess, isPending, handleLoadingComplete, onClose]);

  return {
    step,
    selectedPolicies,
    showLoadingSequence,
    handleTogglePolicy,
    handleContinue,
    handleBack,
    handleSend,
    handleLoadingComplete,
  };
}
